import { Grid } from '@achieve/ascend'
import FacebookIconGray from 'components/Svgs/FacebookIconGray'
import FacebookIconColor from 'components/Svgs/FacebookIconColor'
import FacebookIconWhite from 'components/Svgs/FacebookIconWhite'
import InstagramIconGray from 'components/Svgs/InstagramIconGray'
import InstagramIconColor from 'components/Svgs/InstagramIconColor'
import XIconGray from 'components/Svgs/XIconGray'
import XIconColor from 'components/Svgs/XIconColor'
import YoutubeIconGray from 'components/Svgs/YoutubeIconGray'
import YoutubeIconColor from 'components/Svgs/YoutubeIconColor'
import YoutubeIconWhite from 'components/Svgs/YoutubeIconWhite'
import LinkedInIconGray from 'components/Svgs/LinkedInIconGray'
import LinkedInIconColor from 'components/Svgs/LinkedInIconColor'
import { mainProductConfig } from 'constants/mainProductConfig'

const DEFAULT_COLOR = 'gray'
const WHITE_COLOR = 'white'

const DECORATIVE_ICON_PROPS = {
  'aria-hidden': true,
  focusable: 'false',
}

const SOCIAL_PLATFORMS = [
  { key: 'instagram', name: 'Instagram', domains: ['instagram.com'] },
  { key: 'facebook', name: 'Facebook', domains: ['facebook.com'] },
  { key: 'x', name: 'X', domains: ['x.com', 'twitter.com'] },
  { key: 'youtube', name: 'YouTube', domains: ['youtube.com', 'youtu.be'] },
  { key: 'linkedin', name: 'LinkedIn', domains: ['linkedin.com'] },
]

function findSocialPlatform(url) {
  const hostname = String(url)
    .toLowerCase()
    .match(/^https?:\/\/([^/?#]+)/)?.[1]
    ?.replace(/^www\./, '')

  if (!hostname) return null

  return (
    SOCIAL_PLATFORMS.find(({ domains }) =>
      domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))
    ) || null
  )
}

function FacebookComponent({ defaultColor = 'gray' }) {
  const DefaultIcon = defaultColor === WHITE_COLOR ? FacebookIconWhite : FacebookIconGray

  return (
    <div className="relative flex min-w-[24px] min-h-[24px]">
      <div className="absolute opacity-100 hover:opacity-0 transition-opacity">
        <DefaultIcon {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
      <div className="absolute opacity-0 hover:opacity-100 transition-opacity">
        <FacebookIconColor {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
    </div>
  )
}

function InstagramComponent({ defaultColor = 'gray' }) {
  const DefaultIcon = defaultColor === WHITE_COLOR ? InstagramIconColor : InstagramIconGray
  const defaultStyle = defaultColor === WHITE_COLOR ? { filter: 'brightness(0) invert(1)' } : {}

  return (
    <div className="relative flex min-w-[24px] min-h-[24px]">
      <div
        className={`absolute ${
          defaultColor === WHITE_COLOR ? '' : 'opacity-100 hover:opacity-0'
        } transition-opacity`}
        style={defaultStyle}
      >
        <DefaultIcon {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
      {defaultColor !== WHITE_COLOR && (
        <div className="absolute opacity-0 hover:opacity-100 transition-opacity">
          <InstagramIconColor {...DECORATIVE_ICON_PROPS} height={24} width={24} />
        </div>
      )}
    </div>
  )
}

function XComponent({ defaultColor = 'gray' }) {
  const DefaultIcon = defaultColor === WHITE_COLOR ? XIconColor : XIconGray
  const defaultStyle = defaultColor === WHITE_COLOR ? { filter: 'brightness(0) invert(1)' } : {}

  return (
    <div className="relative flex min-w-[24px] min-h-[24px]">
      <div
        className={`absolute ${
          defaultColor === WHITE_COLOR ? '' : 'opacity-100 hover:opacity-0'
        } transition-opacity`}
        style={defaultStyle}
      >
        <DefaultIcon {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
      {defaultColor !== WHITE_COLOR && (
        <div className="absolute opacity-0 hover:opacity-100 transition-opacity">
          <XIconColor {...DECORATIVE_ICON_PROPS} height={24} width={24} />
        </div>
      )}
    </div>
  )
}

function YouTubeComponent({ defaultColor = 'gray' }) {
  const DefaultIcon = defaultColor === WHITE_COLOR ? YoutubeIconWhite : YoutubeIconGray

  return (
    <div className="relative flex min-w-[24px] min-h-[24px]">
      <div className="absolute opacity-100 hover:opacity-0 transition-opacity">
        <DefaultIcon {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
      <div className="absolute opacity-0 hover:opacity-100 transition-opacity">
        <YoutubeIconColor {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
    </div>
  )
}

function LinkedInComponent({ defaultColor = 'gray' }) {
  const DefaultIcon = defaultColor === WHITE_COLOR ? LinkedInIconColor : LinkedInIconGray
  const defaultStyle = defaultColor === WHITE_COLOR ? { filter: 'brightness(0) invert(1)' } : {}

  return (
    <div className="relative flex min-w-[24px] min-h-[24px]">
      <div className="absolute opacity-100 hover:opacity-0 transition-opacity" style={defaultStyle}>
        <DefaultIcon {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
      <div className="absolute opacity-0 hover:opacity-100 transition-opacity">
        <LinkedInIconColor {...DECORATIVE_ICON_PROPS} height={24} width={24} />
      </div>
    </div>
  )
}

function SocialRenderer(platform, defaultColor) {
  switch (platform) {
    case 'facebook':
      return <FacebookComponent defaultColor={defaultColor} />
    case 'instagram':
      return <InstagramComponent defaultColor={defaultColor} />
    case 'x':
      return <XComponent defaultColor={defaultColor} />
    case 'twitter':
      return <XComponent defaultColor={defaultColor} />
    case 'youtube':
      return <YouTubeComponent defaultColor={defaultColor} />
    case 'linkedin':
      return <LinkedInComponent defaultColor={defaultColor} />
    default:
      return null
  }
}

function AnchorTrack(props) {
  return (
    <a
      onClick={() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        props.trackingCB && props.trackingCB({ href: props.href })
      }}
      href={props.href}
      className={props.className}
      aria-label={props.ariaLabel}
    >
      {props.children}
    </a>
  )
}
function SocialIcons(props) {
  const { items, defaultIconColor = DEFAULT_COLOR } = props
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const brandName = mainProductConfig[siteEnv]?.MainName || mainProductConfig.achieve.MainName

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      spacing={2}
      className={
        defaultIconColor == DEFAULT_COLOR
          ? 'justify-start lg:justify-end'
          : 'justify-center lg:justify-start'
      }
      data-testid="footer-social-links-row"
    >
      {items?.map((item) => {
        const platform = findSocialPlatform(item)
        const Comp = platform ? SocialRenderer(platform.key, defaultIconColor) : null
        if (!Comp) return null
        return (
          <Grid key={item} item>
            <AnchorTrack
              href={item}
              className="flex min-w-[25px] min-h-[24px]"
              trackingCB={props.trackingCB}
              ariaLabel={`${brandName} on ${platform.name}`}
            >
              {Comp}
            </AnchorTrack>
          </Grid>
        )
      })}
    </Grid>
  )
}

export default SocialIcons
